import React from 'react';
import { Panel } from '../../layout/Panel';
import { DataSourceBadge, DATA_SOURCES } from '../../ui/DataSourceBadge';
import { fdiDetailData } from '../../../data/fdiData';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  Cell,
  CartesianGrid,
  ComposedChart,
  Line,
} from 'recharts';
import { getTooltipStyle } from '../../../hooks/useExpandedStyles';
import { TrendingUp, Calendar, ArrowUpRight } from 'lucide-react';

interface FDITrendPanelProps {
  isMinimized?: boolean;
  onMinimize?: () => void;
  isExpanded?: boolean;
}

export const FDITrendPanel: React.FC<FDITrendPanelProps> = ({
  isMinimized,
  onMinimize,
  isExpanded = false,
}) => {
  const { history, quarterly } = fdiDetailData;

  // Calculate CAGR for the history
  const calculateCAGR = (startValue: number, endValue: number, years: number) => {
    return ((Math.pow(endValue / startValue, 1 / years) - 1) * 100).toFixed(1);
  };

  const expandedContent = (
    <div className="flex flex-col h-full gap-8">
      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-terminal-bg rounded-lg p-5">
          <div className="text-sm text-gray-500 uppercase mb-2">2024 Total</div>
          <div className="text-3xl font-bold text-data-green">$11.9B</div>
          <div className="flex items-center gap-1 mt-2 text-data-green text-sm">
            <TrendingUp size={14} />
            <span>+53.6% YoY</span>
          </div>
        </div>
        <div className="bg-terminal-bg rounded-lg p-5">
          <div className="text-sm text-gray-500 uppercase mb-2">10-Year CAGR</div>
          <div className="text-3xl font-bold text-data-amber">
            {calculateCAGR(1.6, 11.9, 9)}%
          </div>
          <div className="text-xs text-gray-500 mt-2">2015-2024</div>
        </div>
        <div className="bg-terminal-bg rounded-lg p-5">
          <div className="text-sm text-gray-500 uppercase mb-2">Peak Year</div>
          <div className="text-3xl font-bold text-white">2024</div>
          <div className="text-xs text-gray-500 mt-2">All-time high</div>
        </div>
        <div className="bg-terminal-bg rounded-lg p-5">
          <div className="text-sm text-gray-500 uppercase mb-2">Cumulative</div>
          <div className="text-3xl font-bold text-data-amber">$32.5B</div>
          <div className="text-xs text-gray-500 mt-2">Since 2015</div>
        </div>
      </div>

      {/* Main Trend Chart - Large */}
      <div className="flex-1 bg-terminal-bg rounded-lg p-6 min-h-[400px]">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-200 uppercase tracking-wide">
              FDI Inflow Trend
            </h3>
            <p className="text-sm text-gray-500 mt-1">Annual foreign direct investment (USD Billions)</p>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded bg-data-green" />
              <span className="text-gray-400">Actual</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded bg-data-amber border-2 border-dashed border-data-amber" />
              <span className="text-gray-400">Forecast</span>
            </div>
          </div>
        </div>
        <div className="h-[350px]">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={history} margin={{ top: 20, right: 30, bottom: 20, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e1e26" />
              <XAxis
                dataKey="year"
                tick={{ fontSize: 14, fill: '#9ca3af' }}
                axisLine={{ stroke: '#1e1e26' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 14, fill: '#9ca3af' }}
                axisLine={false}
                tickLine={false}
                width={50}
                tickFormatter={(v) => `$${v}B`}
                domain={[0, 'auto']}
              />
              <Tooltip
                contentStyle={getTooltipStyle(true)}
                labelStyle={{
                  color: '#ffffff',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  marginBottom: '8px',
                }}
                formatter={(value: number, name: string, props: any) => [
                  `$${value}B`,
                  props.payload.forecast ? 'Forecast' : 'FDI Inflow',
                ]}
                cursor={{ fill: 'rgba(0, 210, 106, 0.1)' }}
              />
              <Bar dataKey="value" radius={[6, 6, 0, 0]} maxBarSize={60}>
                {history.map((entry: any, index: number) => (
                  <Cell
                    key={index}
                    fill={entry.forecast ? '#ffb800' : entry.year === '2024' ? '#00d26a' : '#30363d'}
                    fillOpacity={entry.forecast ? 0.6 : 1}
                    stroke={entry.forecast ? '#ffb800' : entry.year === '2024' ? '#00d26a' : 'none'}
                    strokeWidth={entry.year === '2024' || entry.forecast ? 2 : 0}
                  />
                ))}
              </Bar>
              {/* Trend Line */}
              <Line
                type="monotone"
                dataKey="value"
                stroke="#00a3ff"
                strokeWidth={2}
                dot={false}
                strokeDasharray="5 5"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Quarterly Breakdown */}
      <div className="grid grid-cols-2 gap-6">
        {/* Quarterly Chart */}
        <div className="bg-terminal-bg rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <Calendar size={16} className="text-gray-500" />
            <h3 className="text-base font-semibold text-gray-200 uppercase tracking-wide">
              Quarterly Momentum
            </h3>
          </div>
          <div className="h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={quarterly} margin={{ top: 10, right: 10, bottom: 10, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e1e26" vertical={false} />
                <XAxis
                  dataKey="q"
                  tick={{ fontSize: 12, fill: '#9ca3af' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: '#9ca3af' }}
                  axisLine={false}
                  tickLine={false}
                  width={40}
                  tickFormatter={(v) => `$${v}B`}
                />
                <Tooltip
                  contentStyle={getTooltipStyle(true)}
                  formatter={(value: number) => [`$${value}B`, 'FDI']}
                  cursor={{ fill: 'rgba(0, 210, 106, 0.1)' }}
                />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {quarterly.map((entry, index) => (
                    <Cell
                      key={index}
                      fill={entry.q.includes('24') ? '#00d26a' : '#30363d'}
                      fillOpacity={entry.q === 'Q4 24' ? 1 : 0.7}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* YoY Growth Analysis */}
        <div className="bg-terminal-bg rounded-lg p-6">
          <h3 className="text-base font-semibold text-gray-200 uppercase tracking-wide mb-4">
            Year-over-Year Growth
          </h3>
          <div className="space-y-3">
            {history.slice(-5).map((entry: any, index: number, arr: any[]) => {
              const prevValue = index > 0 ? arr[index - 1].value : null;
              const growth = prevValue ? ((entry.value - prevValue) / prevValue * 100).toFixed(1) : null;
              return (
                <div key={entry.year} className="flex items-center justify-between">
                  <span className="text-gray-400">{entry.year}</span>
                  <div className="flex items-center gap-4">
                    <span className="text-white font-semibold">${entry.value}B</span>
                    {growth && (
                      <span className={`flex items-center gap-1 ${
                        parseFloat(growth) > 0 ? 'text-data-green' : 'text-data-red'
                      }`}>
                        <ArrowUpRight
                          size={14}
                          className={parseFloat(growth) < 0 ? 'rotate-180' : ''}
                        />
                        {growth}%
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Historical Context */}
      <div className="bg-terminal-bg rounded-lg p-6">
        <h3 className="text-base font-semibold text-gray-200 uppercase tracking-wide mb-4">
          Key Investment Milestones
        </h3>
        <div className="grid grid-cols-4 gap-4">
          <div className="border-l-2 border-data-green pl-4">
            <div className="text-xs text-gray-500">2017</div>
            <div className="text-sm text-gray-300 mt-1">Currency liberalization opens market</div>
          </div>
          <div className="border-l-2 border-data-amber pl-4">
            <div className="text-xs text-gray-500">2020</div>
            <div className="text-sm text-gray-300 mt-1">COVID-19 impact, investment dip</div>
          </div>
          <div className="border-l-2 border-data-green pl-4">
            <div className="text-xs text-gray-500">2023</div>
            <div className="text-sm text-gray-300 mt-1">Post-pandemic recovery, $7.7B</div>
          </div>
          <div className="border-l-2 border-data-green pl-4">
            <div className="text-xs text-gray-500">2024</div>
            <div className="text-sm text-gray-300 mt-1">Record year, $11.9B achieved</div>
          </div>
        </div>
      </div>
    </div>
  );

  const compactContent = (
    <div className="flex flex-col h-full p-3">
      {/* Hero Stats Row */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="bg-terminal-bg/60 rounded-lg p-3 text-center border border-terminal-border/50">
          <div className="text-xl font-mono font-bold text-data-green">${(history.find((h: any) => h.year === '2024')?.value || 11.9)}B</div>
          <div className="text-xs text-gray-500">2024 FDI</div>
        </div>
        <div className="bg-terminal-bg/60 rounded-lg p-3 text-center border border-terminal-border/50">
          <div className="text-xl font-mono font-bold text-data-amber">{calculateCAGR(1.6, 11.9, 9)}%</div>
          <div className="text-xs text-gray-500">10Y CAGR</div>
        </div>
        <div className="bg-terminal-bg/60 rounded-lg p-3 text-center border border-terminal-border/50">
          <div className="text-xl font-mono font-bold text-white">$32.5B</div>
          <div className="text-xs text-gray-500">Cumulative</div>
        </div>
      </div>

      {/* Annual Bar Chart */}
      <div className="flex-1 bg-terminal-bg/40 rounded-lg p-3 border border-terminal-border/30 min-h-[140px]">
        <div className="text-xs text-gray-400 uppercase tracking-wider mb-2">Annual FDI Trend</div>
        <ResponsiveContainer width="100%" height="90%">
          <BarChart data={history} margin={{ top: 5, right: 5, bottom: 5, left: -10 }}>
            <XAxis
              dataKey="year"
              tick={{ fontSize: 9, fill: '#6b7280' }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 9, fill: '#6b7280' }}
              axisLine={false}
              tickLine={false}
              width={30}
              tickFormatter={(v) => `$${v}B`}
            />
            <Tooltip
              contentStyle={getTooltipStyle(false)}
              labelStyle={{
                color: '#ffffff',
                fontWeight: 'bold',
                fontSize: '12px',
                marginBottom: '6px',
              }}
              formatter={(value: number, name: string, props: any) => [
                `$${value}B`,
                props.payload.forecast ? 'Forecast' : 'FDI Inflow',
              ]}
              cursor={{ fill: 'rgba(0, 210, 106, 0.1)' }}
            />
            <Bar dataKey="value" radius={[4, 4, 0, 0]}>
              {history.map((entry: any, index: number) => (
                <Cell
                  key={index}
                  fill={entry.forecast ? '#ffb800' : entry.year === '2024' ? '#00d26a' : '#30363d'}
                  fillOpacity={entry.forecast ? 0.6 : 1}
                  stroke={entry.forecast ? '#ffb800' : entry.year === '2024' ? '#00d26a' : 'none'}
                  strokeWidth={entry.year === '2024' || entry.forecast ? 2 : 0}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Legend */}
      <div className="flex justify-center gap-4 mt-2 text-xs">
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 rounded bg-data-green" />
          <span className="text-gray-400">2024 Record</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 rounded bg-data-amber/60 border border-data-amber" />
          <span className="text-gray-400">Forecast</span>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-auto pt-2 border-t border-terminal-border/50 flex items-center justify-between">
        <DataSourceBadge source={DATA_SOURCES.UNCTAD} showLabel size="xs" />
        <span className="text-xs text-gray-500">Dec 2025</span>
      </div>
    </div>
  );

  if (isExpanded) {
    return expandedContent;
  }

  return (
    <Panel
      title="INFLOW TREND"
      subtitle="USD BILLIONS"
      status="static"
      isMinimized={isMinimized}
      onMinimize={onMinimize}
      expandedContent={expandedContent}
    >
      {compactContent}
    </Panel>
  );
};

export default FDITrendPanel;
